import { differenceInCalendarDays } from "date-fns/differenceInCalendarDays";
import { startOfToday } from "date-fns/startOfToday";
import {
  Circle,
  Heart,
  type LucideIcon,
  Mail,
  Package,
  Phone,
  Presentation,
  RotateCw,
  Users,
  Utensils,
} from "lucide-react";
import { useLocaleState, useTranslate } from "ra-core";
import { Badge } from "@/components/ui/badge";
import { cn } from "@/lib/utils";

import { useConfigurationContext } from "../root/ConfigurationContext";
import type { Task } from "../types";
import { formatTaskDueDate } from "./taskDueFormat";
import { isOverdue } from "./tasksPredicate";

/**
 * The cells a task row is built from: what kind of task it is, when it is due,
 * and how the chase is going. Each one is a fixed slot in the row's grid, so
 * fifty rows put the same fact at the same x.
 */

/**
 * A shape per task type.
 *
 * Type is the field a day is batched by -- the calls now, the emails after
 * lunch -- and as a bold word glued to the front of the task text it could only
 * be read one row at a time. Keyed on the lowercased configured value, so a
 * `<CRM>` shipping its own casing ("Call") still lands on the right icon.
 */
const TASK_TYPE_ICONS: Record<string, LucideIcon> = {
  call: Phone,
  email: Mail,
  demo: Presentation,
  lunch: Utensils,
  meeting: Users,
  "follow-up": RotateCw,
  "thank-you": Heart,
  ship: Package,
};

const getTaskTypeIcon = (
  type: string | null | undefined,
): LucideIcon | null => {
  if (!type || type === "none") return null;
  return TASK_TYPE_ICONS[type.toLowerCase()] ?? Circle;
};

export const TaskTypeIcon = ({
  type,
  className,
}: {
  type: string | null | undefined;
  className?: string;
}) => {
  const { taskTypes } = useConfigurationContext();
  const Icon = getTaskTypeIcon(type);

  // An untyped task still pays for the column: the text of every row has to
  // start at the same x, or the rail the eye follows breaks.
  //
  // `type` is re-tested alongside the icon so the label below is a string:
  // getTaskTypeIcon already returns null for a missing one, but that is a
  // decision TypeScript cannot follow back through the lookup.
  if (!Icon || !type) {
    return (
      <span className={cn("size-4 shrink-0", className)} aria-hidden="true" />
    );
  }

  const label =
    taskTypes.find((taskType) => taskType.value === type)?.label ?? type;

  return (
    <span
      title={label}
      className={cn("inline-flex h-5 shrink-0 items-center", className)}
    >
      <span className="sr-only">{label}</span>
      <Icon aria-hidden="true" className="size-4 text-muted-foreground" />
    </span>
  );
};

/**
 * When the task is due, as the number the reader acts on.
 *
 * An overdue row says its age ("23 days late") rather than a date the reader
 * has to subtract today from, and carries the destructive colour. A task with
 * no due date says so instead of rendering a dangling "due" followed by
 * nothing.
 */
export const TaskDueCell = ({
  task,
  timeOnly,
  className,
}: {
  task: Task;
  /** Drop the date, keeping the time, under a heading that names the day. */
  timeOnly?: boolean;
  className?: string;
}) => {
  const translate = useTranslate();
  const [locale] = useLocaleState();

  const cellClassName = cn(
    "min-w-0 shrink-0 whitespace-nowrap tabular-nums @2xl:text-right",
    className,
  );

  if (task.due_date == null) {
    return (
      <span className={cellClassName}>
        <Badge
          variant="outline"
          className="px-1.5 py-0 text-[11px] font-normal text-muted-foreground"
        >
          {translate("resources.tasks.due.unscheduled", { _: "Unscheduled" })}
        </Badge>
      </span>
    );
  }

  // A completed task is not late, whatever its due date says.
  const isLate = task.done_date == null && isOverdue(task.due_date);

  if (isLate) {
    const days = Math.max(
      1,
      differenceInCalendarDays(startOfToday(), new Date(task.due_date)),
    );
    return (
      <span className={cn(cellClassName, "font-medium text-destructive")}>
        {translate("resources.tasks.due.overdue_by", {
          smart_count: days,
          _: "%{smart_count} days late",
        })}
      </span>
    );
  }

  return (
    <span className={cn(cellClassName, "text-muted-foreground")}>
      {formatTaskDueDate(task.due_date, locale, timeOnly)}
    </span>
  );
};

/** A chase is worth escalating well before it is worth writing off. */
const ATTEMPT_WARNING = 3;
const ATTEMPT_CRITICAL = 5;

const AttemptCount = ({ count }: { count: number }) => {
  const translate = useTranslate();
  const label = translate("resources.tasks.attempt.nth", { count });

  return (
    <span
      title={label}
      className={cn(
        "shrink-0 font-medium tabular-nums",
        count >= ATTEMPT_CRITICAL
          ? "text-destructive"
          : count >= ATTEMPT_WARNING
            ? "text-amber-600 dark:text-amber-500"
            : "text-muted-foreground",
      )}
    >
      <span className="sr-only">{label}</span>
      <span aria-hidden="true">
        {translate("resources.tasks.attempt.short", {
          count,
          _: `×${count}`,
        })}
      </span>
    </span>
  );
};

/**
 * How the chase is going: which touch this is, and how the last one went.
 *
 * These are the two fields that decide whether an account gets escalated,
 * handed over or written off, so they get a slot of their own next to the due
 * date instead of trailing a wrapping grey line. The slot is rendered even when
 * it holds nothing, so the rows that do carry a count stay in line.
 */
export const TaskCadence = ({
  task,
  className,
}: {
  task: Task;
  className?: string;
}) => {
  const translate = useTranslate();
  const { taskOutcomes } = useConfigurationContext();

  const attempt =
    task.attempt_number != null && task.attempt_number > 1
      ? task.attempt_number
      : undefined;

  /*
    Reported only when it says something. A first attempt is what every task
    starts as, and "not recorded" is the absence of an outcome rather than one --
    spelling either out on every row would drown the rows that did move.

    The label comes from the translation catalog, falling back to the configured
    one: `taskOutcomes` is a `<CRM>` prop, so an application that replaces the
    outcomes ships its own labels with them.
  */
  const outcome =
    task.outcome && task.outcome !== "none" ? task.outcome : undefined;
  const outcomeLabel = outcome
    ? translate(`resources.tasks.outcome.${outcome}`, {
        _:
          taskOutcomes.find((taskOutcome) => taskOutcome.value === outcome)
            ?.label ?? outcome,
      })
    : undefined;

  return (
    <div
      className={cn(
        // Rendered even when it holds nothing: it is a column, and a column
        // that disappears on the rows with nothing to report takes the rows
        // that do have something out of line.
        "flex min-w-0 items-baseline gap-1.5",
        className,
      )}
    >
      {attempt != null && <AttemptCount count={attempt} />}
      {outcomeLabel && (
        <Badge
          // A bounced address needs the data fixed, not another call.
          variant={outcome === "bounced" ? "destructive" : "secondary"}
          className="min-w-0 truncate px-1.5 py-0 text-[11px] font-normal"
        >
          {outcomeLabel}
        </Badge>
      )}
    </div>
  );
};
